// The weapons lane's slice of the game client: predicted throws and launches, ordnance events,
// per-weapon feel (muzzle style, sound, kick, rumble), the gun rack on the cowboys and the HUD
// fields. client/game.ts calls into this; it owns no rules.

import * as THREE from 'three';
import { Ev, GameEvent } from '../../sim/codec';
import { EL_NAME, gunElem, gunLevel } from '../../sim/elements';
import { ENEMIES } from '../../sim/defs';
import { CANOPY_EL, OKind, handPos, throwVelocity } from '../../sim/arsenal';
import { InputFrame, PlayerState, activeWeaponId, weaponOf } from '../../sim/player';
import { Elem, GRENADES, GRENADE_BANK, GRENADE_MAX, NO_WEAPON, Proj, SPIN_TICKS, W, WEAPONS, WSlot, weaponDef } from '../../sim/weapons';
import { aimDir } from '../../sim/core';
import type { NetEnemy } from '../../sim/protocol';
import type { World } from '../../sim/world';
import type { HudState } from '../render/contracts';
import type { Fx } from '../render/fx/index';
import type { Sfx } from '../audio/sfx';
import { GunRack } from './guns';
import { OrdnanceView } from './ordnance';

/** fx.muzzle / fx.tracer style per weapon: 0 heavy brass + smoke, 1 energy, 2 plasma violet, 3 chunky auto */
const STYLE: Record<number, number> = {
  [W.Starter]: 3, [W.PulseRifle]: 1, [W.AutoMG]: 3, [W.BoltSniper]: 0, [W.Gentlemen]: 0,
  [W.BigIron]: 0, [W.DuelBlaster]: 2, [W.Akimbo]: 3, [W.Scoundrel]: 0, [W.Turret]: 3,
};
const SFX: Record<number, [string, number, number]> = {
  [W.Starter]: ['shot_phantom', 1.25, 0.8], [W.PulseRifle]: ['shot_valkyrie', 1, 0.9], [W.AutoMG]: ['shot_juggernaut', 1, 0.9],
  [W.BoltSniper]: ['sniper_fire', 1, 1], [W.Gentlemen]: ['shot_gunslinger', 0.72, 1], [W.BigIron]: ['shot_gunslinger', 1, 0.95],
  [W.DuelBlaster]: ['shot_valkyrie', 0.62, 1], [W.Akimbo]: ['shot_juggernaut', 1.3, 0.75], [W.Scoundrel]: ['shot_gunslinger', 0.6, 1],
};
/** a sound per element proc, from the existing bank: [name, pitch, volume] */
const PROC_SFX: Record<number, [string, number, number]> = {
  [Elem.Fire]: ['explosion', 1.7, 0.3], [Elem.Ice]: ['shield_hit', 1.5, 0.6], [Elem.Shock]: ['sniper_fire', 2.1, 0.35],
  [Elem.Acid]: ['armor', 0.6, 0.6], [Elem.Magnetic]: ['shield_break', 1.3, 0.45],
};
export function weaponStyle(wid: number) { return STYLE[wid] ?? 0; }
export function weaponSfx(wid: number): [string, number, number] {
  const w = WEAPONS[wid];
  if (SFX[wid]) return SFX[wid];
  if (w && (w.proj === Proj.Rocket)) return ['bomb_throw', 0.55, 1];
  if (w && w.proj === Proj.Harpoon) return ['sniper_fire', 0.6, 1];
  return ['bomb_throw', 1, 0.8];
}

/** the element installed on a gun, for the HUD: " · FIRE L2" (sim/elements.ts) */
const elTag = (p: PlayerState, slot: number) => { const el = gunElem(p, slot); return el < 0 ? '' : ` · ${EL_NAME[el]} L${gunLevel(p, slot)}`; };

export class WeaponsClient {
  readonly rack: GunRack;
  readonly ord: OrdnanceView;
  private throwAt = new Map<number, number>();
  private procAt = new Map<number, number>();
  private time = 0;
  private v = new THREE.Vector3();

  constructor(scene: THREE.Scene, world: World, private fx: Fx | null, private sfx: Sfx) {
    this.rack = new GunRack(scene);
    this.ord = new OrdnanceView(scene, world, fx);
  }

  throwAge(id: number) { return this.time - (this.throwAt.get(id) ?? -99); }

  update(dt: number, displayTick: (f: { tick0: number; catchup: number; shownAt: number }) => number, enemies: NetEnemy[], n: number) {
    this.time += dt;
    this.ord.update(dt, displayTick, enemies, n);
  }

  /** your own cowboy's predicted throw / ordnance launch */
  predicted(p: PlayerState, inp: InputFrame, tick: number, threw: boolean, shot: boolean) {
    if (threw) {
      this.throwAt.set(p.id, this.time);
      const hp = handPos(p), v = throwVelocity(inp.yaw, inp.pitch);
      this.ord.predict(OKind.Grenade, p.nade, p.id, tick, hp.x, hp.y, hp.z, v.vx, v.vy, v.vz);
      this.sfx.play('bomb_throw', hp, { volume: 0.7 });
    }
    if (shot) {
      const w = weaponOf(p);
      if (w.proj === Proj.Hitscan || w.proj === Proj.Turret) return;
      const hp = handPos(p);
      if (w.proj === Proj.Firework || w.proj === Proj.Beacon) {
        const v = throwVelocity(inp.yaw, inp.pitch);
        this.throwAt.set(p.id, this.time);
        this.ord.predict(w.proj === Proj.Beacon ? OKind.Beacon : OKind.Shell, w.id, p.id, tick, hp.x, hp.y, hp.z, v.vx, v.vy, v.vz);
      } else if (w.proj === Proj.Rocket && w.id !== W.BadIdea) {
        const d = aimDir(inp.yaw, inp.pitch, { x: 0, y: 0, z: 0 });
        this.ord.predict(OKind.Rocket, w.id, p.id, tick, hp.x, hp.y, hp.z, d.x * w.projSpeed, d.y * w.projSpeed, d.z * w.projSpeed);
      } else if (w.proj === Proj.Harpoon) {
        const d = aimDir(inp.yaw, inp.pitch, { x: 0, y: 0, z: 0 });
        this.ord.predict(OKind.Harpoon, w.id, p.id, tick, hp.x, hp.y, hp.z, d.x * w.projSpeed, d.y * w.projSpeed + 1, d.z * w.projSpeed);
      }
      const [s, pitch, vol] = weaponSfx(w.id);
      this.sfx.play(s, hp, { pitch, volume: vol });
    }
  }

  /** R1 held on a fused grenade: the pin's out, a tick every half second, faster at the end */
  cooking(p: PlayerState, cookedOff: boolean, input: { rumble(s: number, w: number, ms: number): void }) {
    if (cookedOff) { input.rumble(1, 1, 380); return; }
    const fuse = (GRENADES[p.nade]?.fuse ?? 0) * 60;
    if (fuse <= 0) return;
    const left = fuse - p.cookT;
    const every = left < 45 ? 8 : 30;
    if (p.cookT === 1) this.sfx.play('reload', undefined, { pitch: 1.9, volume: 0.5 }); // pin
    else if (p.cookT % every === 0) { this.sfx.play('reload', undefined, { pitch: left < 45 ? 2.4 : 2.1, volume: 0.35 }); if (left < 45) input.rumble(0.15, 0.3, 40); }
  }

  /** Returns true if the event was the weapons lane's. */
  onEvent(e: GameEvent, myId: number, simTick: number, enemyPos: (id: number, out: THREE.Vector3) => THREE.Vector3 | null): boolean {
    const f = e.f;
    switch (e.t) {
      case Ev.OrdSpawn: {
        this.ord.spawn(f, simTick);
        if (f[10] !== myId) {
          const k = f[1];
          if (k === OKind.Grenade || k === OKind.Shell || k === OKind.Beacon) { this.throwAt.set(f[10], this.time); this.sfx.play('bomb_throw', { x: f[4], y: f[5], z: f[6] }, { volume: 0.5 }); }
          else if (k === OKind.Rocket || k === OKind.BadIdea || k === OKind.Harpoon) { const [s, p] = weaponSfx(f[2]); this.sfx.play(s, { x: f[4], y: f[5], z: f[6] }, { pitch: p, volume: 0.7 }); }
        }
        return true;
      }
      case Ev.OrdEnd: this.ord.end(f); return true;
      case Ev.OrdRest: this.ord.rest(f, simTick); return true;
      case Ev.Crate: {
        this.ord.crate(f);
        if (!f[5] && f[6] === myId) this.sfx.play('reload', undefined, { pitch: 0.8 });
        return true;
      }
      case Ev.Turret: this.ord.turret(f); this.sfx.play('reload', { x: f[1], y: f[2], z: f[3] }, { pitch: 0.6 }); return true;
      case Ev.TurretShot: {
        const at = this.ord.turretPos(f[0]);
        if (f[5] !== 255) this.sfx.play('shot_juggernaut', at, { volume: 0.4, pitch: 1.4 });
        else this.sfx.play('reload', at, { volume: 0.5, pitch: 0.5 }); // sentry powers down
        this.ord.turretShot(f);
        return true;
      }
      case Ev.Proc: {
        const p = enemyPos(f[0], this.v);
        if (p) {
          this.ord.proc(p, f[1]);
          const snd = PROC_SFX[f[1]], last = this.procAt.get(f[1]) ?? -1;
          if (snd && this.time - last > 0.15) { this.procAt.set(f[1], this.time); this.sfx.play(snd[0], p, { pitch: snd[1], volume: snd[2] }); }
        }
        return true;
      }
      case Ev.Throw: if (f[0] !== myId) this.throwAt.set(f[0], this.time); return true;
      case Ev.Barrel: this.ord.barrel(f); return true;
      case Ev.Strongbox: this.ord.strongbox(f, myId); if (f[3] === 0 && f[4] === myId) this.sfx.play('reload', this.v.set(f[0], f[1], f[2]), { volume: 0.6, pitch: 0.6 }); return true;
      case Ev.Hazard:
        if (f[1] >= CANOPY_EL) { this.ord.canopy(f[0], f[1] - CANOPY_EL, f[2], f[3], f[4], f[5], f[6] / 60); return true; }
        return false;
    }
    return false;
  }

  /** HUD fields for your loadout */
  hud(s: HudState, me: PlayerState) {
    const w = weaponOf(me);
    s.weaponName = w.short + elTag(me, me.wSlot);
    s.magMax = w.mag;
    s.reloadFrac = me.reloadT > 0 ? 1 - me.reloadT / w.reloadTicks : -1;
    s.slotIndex = me.wSlot;
    const g = GRENADES[me.nade];
    s.grenadeName = g ? g.name.replace(' Grenade', '').toUpperCase() + (me.nadeLevel >= 2 ? ` L${me.nadeLevel}` : '') : '';
    s.grenades = me.nades; s.grenadeMax = GRENADE_MAX; s.grenadeBank = me.nadeBank / GRENADE_BANK;
    const other = me.wSlot === WSlot.Primary ? me.w1 : me.w0;
    s.otherWeaponName = weaponDef(other).short + elTag(me, me.wSlot === WSlot.Primary ? 1 : 0);
    if (me.w2 !== NO_WEAPON) {
      const sw = weaponDef(me.w2);
      s.specialName = sw.short;
      s.specialAmmo = me.wSlot === WSlot.Special ? me.mag : me.mag2;
      s.specialBank = sw.recharge > 0 ? Math.min(1, me.bank / sw.recharge) : 0;
    } else { s.specialName = ''; s.specialAmmo = 0; s.specialBank = 0; }
    s.chargeFrac = w.fire === 3 && me.chargeT > 0 ? Math.min(1, me.chargeT / w.charge) : -1;
    s.cookFrac = me.cookT > 0 && g?.fuse ? Math.min(1, me.cookT / (g.fuse * 60)) : -1;
    // the held gun's moving parts read your exact rounds, spin and charge
    this.rack.local(me.id, me.mag, me.spin / SPIN_TICKS, w.fire === 3 && w.charge > 0 ? Math.min(1, me.chargeT / w.charge) : 0);
  }

  /** what the cowboy view should draw in this cowboy's hands */
  static held(p: PlayerState) { return activeWeaponId(p); }

  reset() { this.ord.reset(); }
  /** the posse warped to another island */
  setWorld(w: World) { this.ord.setWorld(w); }

  dispose() { this.ord.dispose(); }
}

export { ENEMIES };
